import { createSelector } from "reselect";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import find from "lodash/find";
import moment from "moment";
import { localeSelector } from "./locale";
import { eligibilitySettingsSelector } from "./eligibilitySettings";
import {
  loggedInUserDetailsSelector,
  userProfileDetailsSelector
} from "./userProfileDetails";
import {
  selectCurrencyCode,
  currencyFormatSelector,
  simReplacementLocalConfigSelector,
  globalConfigSelector
} from "./appSettings";
import {
  simReplacementFormSelector,
  workflowSelector,
  deliveryInfoSelector,
  orderSummarySelector,
  orderOttRequestSelector,
  userOrdersSelector,
  selectedActiveLineSelector
} from "ducks/selectors";
import {
  SIM_TYPES,
  SIM_TYPE_SKUS,
  SHIPMENT_METHOD,
  ORDER_TYPES,
  TELCO_ACTIVATION_SUCCESS,
  E_SIM_DELIVERY_CONFIG_TYPE,
  TELCO_ACTIVATION_FAILED
} from "utils/constants";
import { orderSelector } from "./order";
import {
  getURLParams,
  urlParam,
  formatCurrency,
  getValueOrLabel
} from "utils/helpers";
import { getChannel } from "utils/localStorage";

// ***************************************************************
// **************** Service Requests selectors *******************
// ***************************************************************
export const pastServiceRequestsDomain = state =>
  get(state?.api, "serviceRequests.data", []);

export const simReplacementOrderDomain = state =>
  get(state, "api.order.data", {});

export const eSimDeliveryConfig = state =>
  get(state, "api.eSimDeliveryConfig.data", {});

export const simReplacementOrderRefSelector = createSelector(
  simReplacementOrderDomain,
  orderData => get(orderData, "orderReference")
);

export const eSimDeliverySpecificationsSRSelector = createSelector(
  eSimDeliveryConfig,
  deliveryConfig => {
    return get(deliveryConfig, "deliverySpecifications", []);
  }
);

export const replaceESimDeliverySelector = createSelector(
  eSimDeliverySpecificationsSRSelector,
  deliverySpecifications => {
    return (
      find(deliverySpecifications, [
        "orderType",
        E_SIM_DELIVERY_CONFIG_TYPE.replacement
      ])?.deliveryNeeded === true
    );
  }
);

export const haveServiceRequestSelector = createSelector(
  eligibilitySettingsSelector,
  eligibilitySettings =>
    !!Object.keys(eligibilitySettings)?.find(
      setting => eligibilitySettings[setting].eligible
    )?.length
);

export const simReplaceRequestSelector = createSelector(
  eligibilitySettingsSelector,
  eligibilitySettings => {
    if (!isEmpty(eligibilitySettings)) {
      const simReplacementRequest = get(
        eligibilitySettings,
        "sim_replacement",
        {}
      );
      return (
        !isEmpty(simReplacementRequest) && {
          request: "sim_replacement",
          eligible: simReplacementRequest.eligible,
          action: simReplacementRequest.action,
          configuration: simReplacementRequest.configuration
        }
      );
    }
  }
);

export const simReplacementConfigurationSelector = createSelector(
  simReplaceRequestSelector,
  simReplaceRequest => get(simReplaceRequest, "configuration", {})
);

export const simReplacementProofSelector = createSelector(
  simReplacementConfigurationSelector,
  config => get(config, "proofTypes", [])
);

export const simReplacementProofTypesSelector = createSelector(
  simReplacementProofSelector,
  proofs => {
    return proofs.map(proof => proof.type);
  }
);

export const isSimReplacementProofNeedSelector = createSelector(
  simReplacementProofSelector,
  proof => proof.length !== 0
);

export const simReplacementReasonsSelector = createSelector(
  simReplacementConfigurationSelector,
  localeSelector,
  (config, locale) => {
    return get(config, "simChangeReasons", [])
      .filter(reason => reason.enabled)
      .map(reason => ({
        value: reason.key,
        label: get(locale, reason.key, reason.key)
      }));
  }
);

export const planCategorySelector = createSelector(
  userOrdersSelector,
  userOrders => {
    const planOrder = userOrders.find(
      userOrder =>
        get(userOrder, "order_type") === ORDER_TYPES.SIM_ONLY &&
        (get(userOrder, "state") === TELCO_ACTIVATION_SUCCESS ||
          get(userOrder, "state") === TELCO_ACTIVATION_FAILED)
    );
    const isPostpaid = get(planOrder, "postpaid");
    return isPostpaid ? "POSTPAID" : "PREPAID";
  }
);

export const simReplacementFeeSelector = createSelector(
  simReplacementConfigurationSelector,
  simReplacementFormSelector,
  currencyFormatSelector,
  planCategorySelector,
  (config, form, currencyFormat, planCategory) => {
    const simType = get(form, "sim_type.value", SIM_TYPES.E_SIM);
    const paymentRequired = get(config, "paymentRequired", true);
    let replacementFee = {};
    if (paymentRequired) {
      if (simType === SIM_TYPES.E_SIM) {
        replacementFee = find(config.fees, {
          inventoryType: "esim",
          productCategory: planCategory
        });
      } else if (
        simType === SIM_TYPES.PHYSICAL_SIM ||
        simType.toLowerCase() === "physical sim"
      ) {
        replacementFee = find(config.fees, {
          inventoryType: "physical_sim",
          productCategory: planCategory
        });
      }
      replacementFee = {
        ...replacementFee,
        type: "REPLACEMENT_FEE",
        displayPrice: formatCurrency(
          get(replacementFee, "price"),
          currencyFormat
        )
      };
    }

    return replacementFee;
  }
);

export const simReplacementDeliveryFeeSelector = createSelector(
  deliveryInfoSelector,
  currencyFormatSelector,
  simReplacementFormSelector,
  (deliveryInfo, currencyFormat, form) => {
    const simType = get(form, "sim_type.value", SIM_TYPES.E_SIM);
    const shipmentMethod = get(
      deliveryInfo,
      "shippingMethod.name",
      SHIPMENT_METHOD.DOORSTEP_DELIVERY
    );

    let deliveryFee = {
      price: 0
    };
    if (
      shipmentMethod === SHIPMENT_METHOD.DOORSTEP_DELIVERY &&
      (simType === SIM_TYPES.E_SIM ||
        simType.toLowerCase() === SIM_TYPES.ESIM.toLowerCase())
    ) {
      deliveryFee = { price: get(deliveryInfo, "delivery_slot_fee", 0) };
    } else if (
      shipmentMethod === SHIPMENT_METHOD.DOORSTEP_DELIVERY &&
      (simType === SIM_TYPES.PHYSICAL_SIM ||
        simType.toLowerCase() === "Physical SIM".toLowerCase())
    ) {
      deliveryFee = { price: get(deliveryInfo, "delivery_slot_fee", 0) };
    } else if (
      shipmentMethod === SHIPMENT_METHOD.SELF_PICKUP &&
      (simType === SIM_TYPES.PHYSICAL_SIM ||
        simType.toLowerCase() === "Physical SIM".toLowerCase())
    ) {
      deliveryFee = {
        price: get(deliveryInfo, "selfPickUpLocation.deliveryFee", 0)
      };
    }
    deliveryFee = {
      ...deliveryFee,
      type: "SIM_DELIVERY_CHARGES",
      displayPrice: formatCurrency(get(deliveryFee, "price"), currencyFormat)
    };
    return deliveryFee;
  }
);

export const isSimReplacementFeeInvolvedSelector = createSelector(
  simReplacementFeeSelector,
  simReplacementDeliveryFeeSelector,
  (fee, deliveryFee) => {
    return !!(
      Number(get(fee, "price", 0)) + Number(get(deliveryFee, "price", 0))
    );
  }
);

export const simReplacementOrderParamsSelector = createSelector(
  replaceESimDeliverySelector,
  simReplacementFormSelector,
  loggedInUserDetailsSelector,
  userProfileDetailsSelector,
  simReplacementFeeSelector,
  simReplacementDeliveryFeeSelector,
  selectCurrencyCode,
  workflowSelector(),
  deliveryInfoSelector,
  orderSummarySelector,
  orderOttRequestSelector,
  globalConfigSelector,
  simReplacementConfigurationSelector,
  selectedActiveLineSelector,
  (
    isReplaceESimDeliveryNeeded,
    form,
    user,
    profile,
    fee,
    delivery_fee,
    currencyCode,
    workflow,
    deliveryDetails,
    orderSummaryFormData,
    requestId,
    globalConfig,
    simReplacementConfig,
    selectedActiveLine
  ) => {
    const currentWorkflow = workflow.find(
      page => page.path === window.location.pathname
    );
    const formMappingData = currentWorkflow?.formDocMapping || {};
    const currentOtp = orderSummaryFormData?.current_otp || [];

    let simTypeObj = {};
    let document_id = form?.identificationNumber || form?.document_id;
    let document_expiry_date =
      form?.document_identificationNumberExpiryDate ||
      form?.document_expiry_date;
    if (
      form?.sim_type?.value === "PHYSICAL_SIM" ||
      form?.sim_type?.name === "PHYSICAL_SIM".toLowerCase()
    ) {
      simTypeObj = {
        sku: "",
        name: "PHYSICAL_SIM",
        type: "SIM_TYPE",
        identifiedBy: "",
        properties: []
      };
    } else {
      simTypeObj = {
        sku: SIM_TYPE_SKUS.ESIM,
        name: "ESIM",
        type: SIM_TYPES.SIM_TYPE,
        identifiedBy: "",
        properties: []
      };
    }

    document_expiry_date = document_expiry_date
      ? moment(
          document_expiry_date
            .split("-")
            .reverse()
            .join("-")
        ).format("YYYY-MM-DD")
      : "";

    const documentItems = [
      {
        sku: "",
        name: "DOCUMENT_FRONT_IMAGE",
        type: "DOCUMENT",
        identifiedBy: formMappingData?.DOCUMENT_FRONT_IMAGE || "frontImage",
        properties: []
      },
      {
        sku: "",
        name: "DOCUMENT_BACK_IMAGE",
        type: "DOCUMENT",
        identifiedBy: formMappingData?.DOCUMENT_BACK_IMAGE || "backImage",
        properties: []
      },
      {
        sku: "",
        name: "DOCUMENT_SELFIE_IMAGE",
        type: "DOCUMENT",
        identifiedBy: formMappingData?.DOCUMENT_SELFIE_IMAGE || "selfieImage",
        properties: []
      }
    ];
    let orderParams = {
      channel: getChannel(),
      serviceAccount: selectedActiveLine?.value?.service_instance_no,
      orderType: "Service Order",
      auth: {
        requestId,
        verificationCode: Array.isArray(currentOtp)
          ? currentOtp.join("")
          : currentOtp
      },
      items: [
        {
          sku: "",
          name: "SIM_REPLACEMENT",
          type: "REQUEST",
          identifiedBy: "",
          properties: [
            {
              key: "SIM_REPLACEMENT_REASON",
              value: form?.reason?.value || ""
            },
            {
              key: "NATIONALITY",
              value: form?.nationality?.value || ""
            },
            {
              key: "DOCUMENT_TYPE",
              value: document_id ? form?.doc_type?.value || "" : ""
            },
            {
              key: "DOCUMENT_ID",
              value: document_id || ""
            },
            {
              key: "DOCUMENT_EXPIRY_DATE",
              value: document_expiry_date || ""
            },
            {
              key: "TEMPORARY_CONTACT_NUMBER",
              value: form?.temporary_contact_number || ""
            }
          ]
        },
        {
          sku: "",
          name: "REPLACEMENT_FEE",
          type: "REQUEST_FEE",
          identifiedBy: get(fee, "price", ""),
          properties: [
            {
              key: "CURRENCY",
              value: currencyCode
            }
          ]
        },
        {
          sku: "",
          name: "SIM_DELIVERY_CHARGES",
          type: "REQUEST_FEE",
          identifiedBy: Number(delivery_fee?.price) || 0,
          properties: [
            {
              key: "CURRENCY",
              value: currencyCode
            }
          ]
        }
      ],
      userDetails: {
        firstName: user.first_name,
        middleName: user.middle_name,
        lastName: user.last_name,
        contactNumber: selectedActiveLine?.value?.phone_no,
        email: user.email,
        dateOfBirth: user?.dob
          ? moment(user.dob.timestamp * 1000).format("DD-MM-YYYY")
          : ""
      },
      billingDetails: {
        firstName: user.first_name,
        middleName: user.middle_name,
        lastName: user.last_name,
        addressLine1:
          user.billing_address?.address_line_1 ||
          selectedActiveLine?.value?.circles_info?.billing_address?.street,
        addressLine2:
          user.billing_address?.address_line_2 ||
          selectedActiveLine?.value?.circles_info?.billing_address?.building_no,
        addressLine3: "",
        city:
          user.billing_address?.city ||
          selectedActiveLine?.value?.circles_info?.billing_address?.city,
        state:
          user.billing_address?.state ||
          selectedActiveLine?.value?.circles_info?.billing_address?.state,
        country:
          user.billing_address?.country ||
          selectedActiveLine?.value?.circles_info?.billing_address?.country,
        postalCode:
          user.billing_address?.postal_code ||
          selectedActiveLine?.value?.circles_info?.billing_address?.post_code
      }
    };
    const isPhysicalSim =
      form?.sim_type?.value === "PHYSICAL_SIM" ||
      form?.sim_type?.name === "PHYSICAL_SIM".toLowerCase() ||
      isReplaceESimDeliveryNeeded;

    const shipmentMethod =
      deliveryDetails?.shippingMethod?.name ||
      SHIPMENT_METHOD.DOORSTEP_DELIVERY;

    if (isPhysicalSim) {
      if (shipmentMethod === SHIPMENT_METHOD.DOORSTEP_DELIVERY) {
        const initialShippingDetails = getPhysicalSimOrderParams(
          deliveryDetails,
          user,
          globalConfig,
          selectedActiveLine
        );
        orderParams["initialShippingDetails"] = {
          ...initialShippingDetails
        };
      } else if (shipmentMethod === SHIPMENT_METHOD.SELF_PICKUP) {
        orderParams.initialShippingDetails = {
          deliveryMethod: SHIPMENT_METHOD.SELF_PICKUP,
          postalCode: deliveryDetails?.selfPickUpLocation?.zipcode,
          selfPickUpLocationId: deliveryDetails?.selfPickUpLocation?.id
        };
      }

      orderParams.items = [...orderParams.items, simTypeObj];
    } else {
      orderParams.items = [...orderParams.items, simTypeObj];
    }

    const docTypeValue = form?.doc_type?.value || "";
    const docProofType = find(
      simReplacementConfig?.proofTypes,
      proof => proof?.type === docTypeValue
    );
    const docTypeAttachments = docProofType?.document?.attachments || [];
    const isDocumentsRequired = docTypeAttachments.length !== 0;

    if (isDocumentsRequired) {
      orderParams.items.push(...documentItems);
    }

    return orderParams;
  }
);

const getPhysicalSimOrderParams = (
  deliveryDetails,
  user,
  globalConfig,
  selectedActiveLine
) => {
  const physicalOrderDetails = {
    deliveryMethod: SHIPMENT_METHOD.DOORSTEP_DELIVERY,
    addressLine1:
      getValueOrLabel(deliveryDetails?.delivery_street_name) ||
      getValueOrLabel(deliveryDetails?.delivery_address_line_1),
    addressLine2:
      getValueOrLabel(deliveryDetails?.delivery_unit_no) ||
      getValueOrLabel(deliveryDetails?.delivery_address_line_2),
    addressLine3: "",
    city:
      getValueOrLabel(deliveryDetails?.delivery_city) ||
      getValueOrLabel(deliveryDetails?.delivery_city?.value),
    state: deliveryDetails?.delivery_state || "",
    province: getValueOrLabel(deliveryDetails?.delivery_province?.value),
    area: getValueOrLabel(deliveryDetails?.delivery_area?.value),
    landmark: getValueOrLabel(deliveryDetails?.delivery_landmark),
    country:
      deliveryDetails?.delivery_country?.value ||
      user.billing_address?.country ||
      selectedActiveLine?.value?.circles_info?.billing_address?.country ||
      globalConfig?.country_code,
    postalCode: getValueOrLabel(deliveryDetails?.delivery_zip_code),
    specialInstructions: deliveryDetails?.delivery_instructions,
    slot: {
      date: moment(deliveryDetails?.delivery_date).format("DD-MM-YYYY"),
      slotId: deliveryDetails?.delivery_slot_id,
      courierName: deliveryDetails?.delivery_slot
    }
  };
  return physicalOrderDetails;
};

export const isSimReplacementRequestNextButtonEnableSelector = createSelector(
  simReplacementFormSelector,
  isSimReplacementProofNeedSelector,
  simReplacementProofSelector,
  (form, proofNeeded, proofs) => {
    if (!form?.reason?.value || !form?.sim_type?.value) return false;

    if (proofNeeded) {
      if (
        !form?.nationality?.value ||
        !form?.doc_type?.value ||
        !form[form?.doc_type?.value] ||
        !form?.expiry_date
      )
        return false;

      let haveAllProof = true;

      proofs.forEach(proof => {
        proof.document.attachments.forEach(attachment => {
          if (!form[attachment]) {
            haveAllProof = false;
          }
        });
      });

      return haveAllProof;
    } else {
      return true;
    }
  }
);

export const isSimReplacementRequestKycNextButtonEnableSelector = createSelector(
  simReplacementFormSelector,
  isSimReplacementProofNeedSelector,
  simReplacementProofSelector,
  (form, proofNeeded, proofs) => {
    if (proofNeeded) {
      if (
        !form?.doc_type?.value ||
        !form?.document_id ||
        !form?.document_expiry_date
      )
        return false;

      let haveAllProof = true;

      proofs.forEach(proof => {
        if (proof?.document) {
          proof.document.attachments.forEach(attachment => {
            if (!form[attachment]) {
              haveAllProof = false;
            }
          });
        }
      });

      return haveAllProof;
    } else {
      return true;
    }
  }
);

export const currentOrderReferenceSelector = createSelector(
  orderSelector,
  order => {
    const { orderReference } = getURLParams();

    if (orderReference) return orderReference;
    return get(order, "orderReference", null);
  }
);

export const simReplacementDocTypeSelector = createSelector(
  simReplacementConfigurationSelector,
  localeSelector,
  simReplacementLocalConfigSelector,
  (config, locale, localConfig) => {
    const proofTypes = get(config, "proofTypes", []);
    const unusedDocTypes = get(localConfig, "unused_doc_types", ["selfie"]);

    return proofTypes
      .filter(proof => !unusedDocTypes.includes(proof.type))
      .map(proof => ({
        label: get(locale, proof.type, proof.type),
        value: proof.type.toLowerCase()
      }));
  }
);

export const pastServiceRequestsSelector = createSelector(
  pastServiceRequestsDomain,
  pastServiceRequests => pastServiceRequests
);

export const pastServiceRequestTypeSelector = createSelector(
  pastServiceRequestsSelector,
  requests => {
    const request_id = urlParam("request_id");
    return requests.find(request => request.requestID === request_id)
      ?.requestType;
  }
);

// ***************************************************************
// **************** Service Requests makeSelect ******************
// ***************************************************************
export const makeSelectHaveServiceRequest = () => haveServiceRequestSelector;
export const makeSelectSimReplaceRequest = () => simReplaceRequestSelector;
export const makeSelectSimReplacementConfiguration = () =>
  simReplacementConfigurationSelector;
export const makeSelectSimReplacementProof = () => simReplacementProofSelector;
export const makeSelectIsSimReplacementProofNeed = () =>
  isSimReplacementProofNeedSelector;
export const makeSelectSimReplacementReasons = () =>
  simReplacementReasonsSelector;
export const makeSelectSimReplacementFee = () => simReplacementFeeSelector;

export const makeSelectSimReplacementDeliveryFee = () =>
  simReplacementDeliveryFeeSelector;

export const makeSelectIsSimReplacementFeeInvolved = () =>
  isSimReplacementFeeInvolvedSelector;

export const makeSelectSimReplacementOrderRef = () =>
  simReplacementOrderRefSelector;

export const makeSelectPlanCategory = () => planCategorySelector;

export const makeSelectSimReplacementProofTypes = () =>
  simReplacementProofTypesSelector;
export const makeSelectSimReplacementOrderParams = () =>
  simReplacementOrderParamsSelector;
export const makeSelectIsSimReplacementRequestNextButtonEnable = () =>
  isSimReplacementRequestNextButtonEnableSelector;
export const makeSelectIsSimReplacementRequestKycNextButtonEnable = () =>
  isSimReplacementRequestKycNextButtonEnableSelector;
export const makeSelectCurrentOrderReference = () =>
  currentOrderReferenceSelector;
export const makeSelectSimReplacementDocType = () =>
  simReplacementDocTypeSelector;
export const makeSelectPastServiceRequests = () => pastServiceRequestsSelector;
export const makeSelectPastServiceRequestType = () =>
  pastServiceRequestTypeSelector;
